import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../config';
import { useAuth } from '../context/AuthContext';
import './HomePage.css';

function HomePage() {
    const navigate = useNavigate();
    const { isLoggedIn, user, accessToken } = useAuth();
    const isTokenStillValid = !isTokenExpired(accessToken);
    
    const [origin, setOrigin] = useState('');
    const [destinations, setDestinations] = useState(['']);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [tripTypes, setTripTypes] = useState([]);
    const [tripPace, setTripPace] = useState('');
    const [originAutocompleteResults, setOriginAutocompleteResults] = useState([]);
    const [destinationAutocompleteResults, setDestinationAutocompleteResults] = useState([]); // ✅ Now an array of arrays
    const [isLoading, setIsLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
   
    function isTokenExpired(token) {
      if (!token) return true;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp) return true;
        return Date.now() >= payload.exp * 1000;
      } catch {
       
        return true;
      }
    }

    const availableTripTypes = [
        "Tourist Attractions",
        "Cultural & Historical",
        "Nature & Outdoors",
        "Adventure & Thrill",
        "Nightlife & Entertainment",
        "Family & Kid-Friendly",
        "Relax & Wellness"
      ];
      

    const originRef = useRef(null);
    const destinationRefs = useRef([]);

    const handleDestinationChange = useCallback((index, value) => {
        const updatedDestinations = [...destinations];
        updatedDestinations[index] = value;
        setDestinations(updatedDestinations);
    }, [destinations]);
    const handleDeleteDestination = (index) => {
        setDestinations((prev) => prev.filter((_, i) => i !== index));
        setDestinationAutocompleteResults((prevResults) => prevResults.filter((_, i) => i !== index));
      };
      
    const handleTripTypeToggle = (type) => {
        setTripTypes((prev) =>
          prev.includes(type)
            ? prev.filter((t) => t !== type)
            : [...prev, type]
        );
      };
   const toCityStateCountryLabel = (description = "") => {
  const parts = description.split(",").map(p => p.trim()).filter(Boolean);

  const city = parts[0] || "";
  const country = parts.length >= 2 ? parts[parts.length - 1] : "";
  const region = parts.length >= 3 ? parts[1] : ""; // state/province is usually second

  if (city && region && country) return `${city}, ${region}, ${country}`;
  if (city && country) return `${city}, ${country}`;
  return city;
};


    const handleSearchChange = async (event, isDestination = false, index = null) => {
        const input = event.target.value;
        const trimmed = input.trim();

// ✅ Trigger autocomplete only after 3 characters
if (trimmed.length < 3) {
  if (isDestination) {
    // keep typed value, but clear only this destination's dropdown
    handleDestinationChange(index, input);
    setDestinationAutocompleteResults((prevResults) => {
      const updatedResults = [...prevResults];
      updatedResults[index] = [];
      return updatedResults;
    });
  } else {
    // keep typed value, but clear origin dropdown
    setOrigin(input);
    setOriginAutocompleteResults([]);
  }
  return; // ✅ no API call
}

        if (!input) {
            if (isDestination) {
                handleDestinationChange(index, '');
                setDestinationAutocompleteResults((prevResults) => {
                    const updatedResults = [...prevResults];
                    updatedResults[index] = []; // ✅ Clears only the dropdown for this destination
                    return updatedResults;
                });
            } else {
                setOrigin('');
                setOriginAutocompleteResults([]); // ✅ Clears origin dropdown
            }
            return;
        }
    
    
        try {
          const response = await fetch(api.url(`/api/places/google-places-autocomplete?query=${encodeURIComponent(input)}`));
            if (!response.ok) {
                return;
            }
    
            const data = await response.json();
           
    
            if (data.suggestions) {
                if (isDestination) {
                    setDestinationAutocompleteResults((prevResults) => {
                        const updatedResults = [...prevResults];
                        updatedResults[index] = data.suggestions; // ✅ Stores results only for the correct field
                        return updatedResults;
                    });
                } else {
                    setOriginAutocompleteResults(data.suggestions); // ✅ Stores results for origin
                }
            }
        } catch {
        }
    };
    
    const handleSelection = (selection, isDestination = false, index = null) => {
        if (!selection || !selection.description) return;
    
        // ✅ Ensure only cities (localities) are selectable
        if (!selection.types.includes("locality")) {
            
            return;
        }
    
        // ✅ Extract only the city name (first part of the description)
        const formattedName = toCityStateCountryLabel(selection.description);

        if (isDestination && index !== null) {
           
            setDestinations((prevDestinations) => {
                const updatedDestinations = [...prevDestinations];
                updatedDestinations[index] = formattedName;
                return updatedDestinations;
            });
        } else {
            
            setOrigin(formattedName);
        }
    
        setOriginAutocompleteResults([]);
        setDestinationAutocompleteResults([]);
    };
    
    
    const addDestination = () => {
        setDestinations([...destinations, '']);
    };

    useEffect(() => {
        const fetchAutocompleteData = async (input) => {
            
            try {
              const response = await fetch(api.url(`/api/places/google-places-autocomplete?query=${encodeURIComponent(input)}`));
                
                const data = await response.json();
                
                return data.suggestions || [];
            } catch  {
                
                return [];
            }
        };
        
        
        const initializeAutocomplete = () => {
            if (!window.google) return;
    
            const originAutocomplete = new window.google.maps.places.Autocomplete(originRef.current, {
                types: ['(regions)']
            });
            originAutocomplete.addListener('place_changed', async () => {
                const place = originAutocomplete.getPlace();
                if (!place || !place.formatted_address) return;
            
                if (place.types && place.types.includes("locality")) {
                    setOrigin(place.formatted_address);
                } else {
                    const cities = await fetchAutocompleteData(place.formatted_address);
                    if (cities.length > 0) {
                        setOrigin(cities[0].description);
                    }
                }
            });
            
    
            destinationRefs.current.forEach((inputRef, index) => {
                if (inputRef) {
                    const destAutocomplete = new window.google.maps.places.Autocomplete(inputRef, {
                        types: ['(regions)']
                    });
                    destAutocomplete.addListener('place_changed', async () => {
                        const place = destAutocomplete.getPlace();
                        if (!place || !place.formatted_address) return;
                    
                        if (place.types && place.types.includes("locality")) {
                            handleDestinationChange(index, place.formatted_address);
                        } else {
                            const cities = await fetchAutocompleteData(place.formatted_address);
                            if (cities.length > 0) {
                                handleDestinationChange(index, cities[0].description);
                            }
                        }
                    });
                    
                }
            });
        };
    
        initializeAutocomplete();
    }, [destinations.length, handleDestinationChange]);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (!event.target.closest('.header')) {
          setMenuOpen(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, []);
    

    const handleGenerateItinerary = async () => {
        if (!origin.trim() || destinations.some(dest => !dest.trim()) || !startDate || !endDate || tripTypes.length === 0 || !tripPace) {
          alert('Please fill in all fields before generating the itinerary.');
          return;
        }
      
        setIsLoading(true); // 🟡 Show loading spinner
      
        const totalDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;
        const defaultDaysPerCity = Math.floor(totalDays / destinations.length);
        const daysPerCity = destinations.reduce((acc, city, idx) => {
          acc[city] = defaultDaysPerCity;
          if (idx === 0) acc[city] += totalDays % destinations.length;
          return acc;
        }, {});
      
        // Normalize tripTypes to match backend schema
        const normalizedTripTypes = tripTypes.map((type) =>
        type.trim().toLowerCase()
        .replace(/\s*&\s*/g, '-')   // ' & ' -> '-'
        .replace(/\s+/g, '-')       // spaces -> '-'
        );
  
       const tripData = {
       origin_city: origin,
       destinations,
       trip_types: normalizedTripTypes,
       pace: tripPace,
       start_date: startDate,
       end_date: endDate,
        daysPerCity
       };
       
      
        try {
         const response = await fetch(api.url('/api/itinerary/generate'), {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  },
  body: JSON.stringify(tripData)
});
      
          if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
          }
      
          const data = await response.json();
      
          navigate("/itinerary", {
            state: {
              tripData: {
                origin: tripData.origin_city,
                destinations: tripData.destinations,
                startDate: tripData.start_date,
                endDate: tripData.end_date,
                tripType: tripData.trip_types,
                tripPace: tripData.pace
              },
              itineraryData: data.itinerary,
                explore: data.explore || [],
              daysPerCity: tripData.daysPerCity
            }
          });
      
        } catch  {
          
          alert("Failed to generate itinerary. Please try again.");
        } finally {
          setIsLoading(false); // ✅ Hide spinner no matter what
        }
      };
      const resetScroll = useCallback(() => {
        const candidates = [
          window,
          document.scrollingElement,
          document.documentElement,
          document.body,
          document.querySelector('.homepage'),
          document.querySelector('.main-content'),
        ].filter(Boolean);
      
        candidates.forEach((el) => {
          if (el === window) window.scrollTo(0, 0);
          else { try { el.scrollTop = 0; } catch {} }
        });
      }, []);
      
        useLayoutEffect(() => {
            // Prevent browser from restoring old scroll position
            const prev = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
        
            // Reset now and once after paint
            resetScroll();
            requestAnimationFrame(resetScroll);
        
            // Handle bfcache restores (Safari/Firefox)
            const onShow = (e) => {
              if (e.persisted) {
                resetScroll();
                requestAnimationFrame(resetScroll);
             }
           };
          window.addEventListener('pageshow', onShow);
        
            return () => {
              window.removeEventListener('pageshow', onShow);
             window.history.scrollRestoration = prev || 'auto';
           };
         }, [resetScroll]);
      
    
    return (
        <div className="homepage">
            <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>
       <div className="main-content">
   <div className="left-content">
 <div className="intro-section">
  <div className="intro-text">
    <h2>Plan with Ease, Explore with Wonder</h2>

    {isLoggedIn && isTokenStillValid && (
      <h3 className="welcome-message">Welcome, {user?.name}!</h3>
    )}

    <p>
      Plan smarter with Custom Roam’s personalized itineraries,<br />
      Get inspired by authentic experiences shared on Roam Share.
    </p>
    {(!isLoggedIn || !isTokenStillValid) && (
           <button className="login-button" onClick={() => navigate('/login')}>Log in/Sign up</button>
          )}
  </div>

  <div className="intro-image">
  
    <img
      src="/images/planner-map.png"
      alt="Traveler planning route"
      className="intro-illustration"
    />
  </div>
  
</div>


<div className="how-it-works">
  <h3>How It Works</h3>
  <div className="steps">
    <div className="step">
      <img src="/images/icon-step1.png" alt="Step 1" />
      <p><strong>Step 1</strong><br />Choose Destination</p>
    </div>
    <div className="step">
      <img src="/images/icon-step2.png" alt="Step 2" />
      <p><strong>Step 2</strong><br />Pick Dates & Themes</p>
    </div>
    <div className="step">
      <img src="/images/icon-step3.png" alt="Step 3" />
      <p><strong>Step 3</strong><br />Generate Itinerary</p>
    </div>
  </div>
</div>

<div className="why-custom-roam">
  <h3>Why Custom Roam?</h3>
  <div className="features">
    <div className="feature">
      <img src="/images/icon-create.png" alt="Smart Itinerary" />
      <p><strong>Smart Itinerary Creation</strong><br />Auto-generate day-wise plans tailored to your trip type.</p>
    </div>
    <div className="feature">
      <img src="/images/icon-flow.png" alt="Travel Flow" />
      <p><strong>Realistic Travel Flow</strong><br />Balanced schedules with time for attractions, travel.</p>
    </div>
    <div className="feature">
      <img src="/images/icon-customize.png" alt="Customize" />
      <p><strong>Customize Your Way</strong><br />Add, swap, or edit attractions to fit your preferences.</p>
    </div>
    <div className="feature">
      <img src="/images/icon-save.png" alt="Save Itinerary" />
      <p><strong>Save for Later</strong><br />Keep itineraries securely stored for easy access anytime.</p>
    </div>
    <div className="feature">
      <img src="/images/icon-share.png" alt="Share Itinerary" />
      <p><strong>Share with Friends</strong><br />Send your travel plans in one click.</p>
    </div>
    <div className="feature">
      <img src="/images/icon-roamshare.png" alt="Roam Share" />
      <p><strong>Roam Share Stories</strong><br />Explore trips and hidden gems from other travelers.</p>
    </div>
  </div>
</div>
 
  </div>

    <div className="form-card">
     <h3>Plan Your Trip</h3>
    <div className="input-container">
    <input
   type="text"
   placeholder="From (Origin City)"
   value={origin}
   onChange={(e) => {
     setOrigin(e.target.value); // ✅ Ensures typing works
     handleSearchChange(e);
     }}                        
  ref={originRef}
     />
      {/* ✅ Display Autocomplete Suggestions for Origin */}
      {/* ✅ Display Autocomplete Suggestions */}
      {originAutocompleteResults.length > 0 && (
    <div className="autocomplete-dropdown">
    {originAutocompleteResults
    .filter(result => result.types.includes("locality"))
    .slice(0, 10)
    .map((result, i) => (
      <div 
     key={i} 
    className="autocomplete-item"
     onClick={() => handleSelection(result, false)} // ✅ Ensures only origin updates
     >
    {toCityStateCountryLabel(result.description)}

   </div>
  ))}
    </div>
)}
</div>

{destinations.map((destination, index) => (
  <div key={index} className="input-container destination-with-delete">
    <input
    type="text"
   placeholder="To (Destination City)"
  value={destination}
   onChange={(e) => {
  handleDestinationChange(index, e.target.value);
  handleSearchChange(e, true, index);
 }}
   ref={(el) => destinationRefs.current[index] = el}
    />
 {index > 0 && (
  <button
   type="button"
   className="delete-destination-button"
   onClick={() => handleDeleteDestination(index)}
  >
     ✕
   </button>
    )}
  {destinationAutocompleteResults[index] && destinationAutocompleteResults[index].length > 0 && (
     <div className="autocomplete-dropdown">
   {destinationAutocompleteResults[index]
  .filter(result => result.types.includes("locality"))
  .slice(0, 10)
   .map((result, i) => (
     <div
   key={`${index}-${i}`}
    className="autocomplete-item"
     onClick={() => handleSelection(result, true, index)}
     >
      {toCityStateCountryLabel(result.description)}
     </div>
  ))}
      </div>
    )}
  </div>
))}
   <button onClick={addDestination} className="add-button">+ Add Destination</button>

     <div className="date-row">
  <div className="date-input-wrapper">
    <label htmlFor="start-date">Start Date</label>
    <input
  type="date"
  id="start-date"
  value={startDate}
  onChange={(e) => setStartDate(e.target.value)}
  min={new Date().toISOString().split("T")[0]}
/>
  </div>
  <div className="date-input-wrapper">
    <label htmlFor="end-date">End Date</label>
    <input
  type="date"
  id="end-date"
  value={endDate}
  onChange={(e) => setEndDate(e.target.value)}
  min={startDate || new Date().toISOString().split("T")[0]}
/>
  </div>
</div>

  <div className="trip-type-selector">
   <label>Select Trip Themes:</label>
   <div className="trip-type-options">
   {availableTripTypes.map((type) => (
 <div
  key={type}
   className={`trip-type-box ${tripTypes.includes(type) ? 'selected' : ''}`}
    onClick={() => handleTripTypeToggle(type)}
     >
     {type}
   </div>
    ))}
     </div>
    </div>
      <div className="custom-dropdown">
     <select value={tripPace} onChange={(e) => setTripPace(e.target.value)} required>
    <option value="" disabled hidden>Pace of Trip</option>
   <option value="slow-paced">Slow-Paced</option>
    <option value="moderate">Moderate</option>
     <option value="fast-paced">Fast-Paced</option>
  </select>
    <span className="dropdown-arrow">▼</span>
      </div>
   <button onClick={handleGenerateItinerary} className="generate-button">Generate Itinerary</button>
   {isLoading && ( <div className="loading-message">Creating your itinerary, please wait...</div>)}
     </div>
     </div>

     <footer className="homepage-footer">
     <div className="homepage-footer-links">
  <span onClick={() => navigate("/terms")}>
    Terms & Conditions
  </span>

  <span onClick={() => navigate("/privacy-policy")}>
    Privacy Policy
  </span>

  <span onClick={() => navigate("/about-us")}>
    About Us
  </span>

  <span onClick={() => navigate("/support")}>
    Support
  </span>
</div>

  <div className="homepage-footer-copy">
    © {new Date().getFullYear()} Custom Roam. All rights reserved.
  </div>
</footer>
 </div>
);
}

export default HomePage;
